import React, { Component } from 'react';
import styled from 'styled-components';
import {
  attachLocale,
  isSlug,
  makeExternalUrl,
  resolveAccessibleLabel,
  screenReaderTextReplace,
} from '../../../../lib/utils';
import {
  DATA_TRACK_ROLE_CONTAINER,
  DATA_TRACK_TYPE_CTA,
  DATA_TRACK_TYPE_INTERNAL,
  DL_AD_BANNER,
  DOM_AD_LARGE,
} from '../../../../lib/constants';
import Button from '../Button/Button';
import PinterestAnalyticsTracker from './../shared/PinterestAnalyticsTracker';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';
import {
  addItemsToDataLayer,
  dataLayerIdGenerator,
  dataLayerIdGeneratorNewItem,
} from '../../../../lib/dataLayerUtils';
import ArrowRight from '../../../svgs/ArrowRight';
import MilesOffer from '../shared/MilesOffer';
import DateDisplay from '../DateDisplay/DateDisplay';
import PromoBadge from '../PromoBadge/PromoBadge';
import { AdHeaderContainer } from './components';
import ShopNowButton from '../Button/ShopNowButton';

// *** Desktop ***
const AdDiv = styled.div`
  display: flex;
  min-height: ${p => p.theme.unit(42)};
  max-height: ${p => p.theme.unit(68)};
  width: 100%;
  margin-top: ${p => p.theme.unit(1.5)};
  margin-bottom: ${p => p.theme.unit(1.5)};
  border-radius: ${p => p.theme.unit(2)};
  
  border: 1px solid ${({ theme }) => theme.c.border.primary};
  ${({ theme }) => theme['elevation-shadow']['1']}
`;

const AdImg = styled.img`
  height: 100%;
  width: 100%;
  position: absolute;
  object-fit: cover;
  border-radius: 0 16px 16px 0;
`;

const AdShopContainerDiv = styled.div`
  width: 100%;
  display: flex;
  justify-content: ${p => p.isMobile ? 'space-between' : 'flex-start'};
  gap: ${p => p.theme.unit(2)};
  align-items: center;
`;

const AdShopImg = styled.img`
  max-height: 100%;
  max-width: 100%;
`;

const AdShopImgContainerDiv = styled.div`
  height: ${p => p.theme.unit(6)};
  display: flex;
  align-items: center;
`;

const AdColOne = styled.div`
  width: 45%;
`;

const AdBodyDiv = styled.div`
  width: 100%;
  padding: ${p => p.theme.unit(2)};
  box-sizing: border-box;
`;

const AdTagSpan = styled.span`
  display: none;
`;

const HeaderTextH2 = styled.h2`
  ${({ theme }) => theme.typographyRebrand.heading.xl}
  color: ${p => p.theme.c.text.primary};
  padding: ${p => p.theme.unit(2)} 0;
  margin: 0;
`;

const ScreenReaderOnlyH1 = styled.h1`
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const SubHeaderTextDiv = styled.div`
  ${({ theme }) => theme.typographyRebrand.body.md}
  height: ${p => p.theme.unit(12)};
  color: ${p => p.theme.c.text.primary};

  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  @media (min-width: ${p => p.theme.breakpoint.mobile}px) and (max-width: ${p => p.theme.breakpoint.tablet}px) {
    -webkit-line-clamp: 4;
  }
`;

const ButtonRowDiv = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 32px;

  margin-top: 16px;
`;

const ExpiryTextDiv = styled.div`
  padding: ${p => p.theme.unit(2)} 0;
  @media screen and (max-width: ${p => p.theme.breakpoint.mobile}px) {
    padding-top: 16px;
    padding-bottom: ${p => (p.exclusionsApply ? '8px' : '16px')};
  }
`;

const AdImgContainerDiv = styled.div`
  position: relative;
  width: 55%;
`;

// *** Mobile ***
const AdMobileDiv = styled.div`
  width: ${p => p.theme.unit(36)};
  height: auto;
  margin-top: ${p => p.theme.unit(1.5)};
  margin-bottom: ${p => p.theme.unit(1.5)};
  border-radius: 16px;
  ${({ theme }) => theme['elevation-shadow']['1']}
`;

const AdImgMobileDiv = styled.img`
  width: 100%;
  height: auto;
  max-height: 170px;
  object-fit: contain;
  border-radius: 8px 8px 0 0;
`;

const AdShopImgMobileContainerDiv = styled.div`
  height: ${p => p.theme.unit(4)};
  display: flex;
  align-items: center;
`;

const AdBodyMobileDiv = styled.div`
  height: auto;
  padding: 0 16px 8px 16px;
`;

const HeaderTextMobileH2 = styled.h2`
  ${({ theme }) => theme.typographyRebrand.heading.xl}
  color: ${p => p.theme.c.text.primary};
  margin: 0;
  height: auto;
  padding: ${p => p.theme.unit(2)} 0;
`;

const SubHeaderTextMobileDiv = styled(SubHeaderTextDiv)`
  height: ${p => p.theme.unit(15)};
  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
`;

const ButtonColumnDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  height: ${p => p.theme.unit(12.5)};
  padding-top: ${p => (p.isMobile ? 0 : p.theme.unit(2))};
  div {
    display: flex;
    width: 100%;
  }
`;

const ExclusionsTextDiv = styled.div`
  ${({ theme }) => theme.typographyRebrand.body.md}
  color: ${({ theme }) => theme.c.text.primary};

  padding-bottom: 16px;
`;

class AdLarge extends Component {
  constructor(props) {
    super(props);

    const itemSlug = this.props.slug ? this.props.slug : '';
    const dataLayerId = dataLayerIdGenerator(DL_AD_BANNER, itemSlug);
    this.state = {
      dataLayerId,
    };
  }

  // Analytics: Update dataLayer with new items
  componentDidMount() {
    const itemType = DL_AD_BANNER;
    const newItem = dataLayerIdGeneratorNewItem(
      itemType,
      this.props,
      this.state.dataLayerId
    );
    const items = [newItem];
    addItemsToDataLayer(items, itemType);
  }

  render() {
    const {
      imageUrl,
      imageAltText,
      name,
      slug,
      headerText,
      subHeaderText,
      primaryButtonText,
      primaryButtonDescriptiveText,
      primaryButtonUrl,
      secondaryButtonText,
      secondaryButtonDescriptiveText,
      secondaryButtonUrl,
      expirationDate,
      expiryText,
      shop,
      shop: {
        baseMiles,
        amountPerBaseMiles,
        multiplier,
        siteWideMultiplier,
        mediumImageUrl,
        mediumImageAltText,
        slug: shopSlug,
        exclusionsApply,
        config: {
          singularMilesText,
          pluralMilesText,
          singularFlatRateMilesText,
          pluralFlatRateMilesText,
          milesBadgeText,
        },
      },
      tags,
      locale,
      exclusionsApplyText,
      prepositionKey,
      linkTextPrepositions,
    } = this.props;

    const adImgUrl = makeExternalUrl(imageUrl);
    const shopImgUrl = makeExternalUrl(mediumImageUrl);
    const resolvedSecondaryUrl = isSlug(secondaryButtonUrl)
      ? attachLocale(secondaryButtonUrl, locale)
      : makeExternalUrl(secondaryButtonUrl);
    const trackingLocation = `page:${DOM_AD_LARGE}-${slug}`;

    const shopNowButtonProps = {
      size: 'medium',
      theme: 'light',
      buttonText: primaryButtonText,
      accessibleLabel: resolveAccessibleLabel(
        primaryButtonDescriptiveText,
        shop,
        linkTextPrepositions,
        'for', // check
        primaryButtonText,
        screenReaderTextReplace(headerText, locale),
        prepositionKey || 'and'
      ),
      // Resolve interstitial URL params
      shopSlug,
      adSlug: slug,
      locale,
      // Track analytics params
      trackAnalytics: (redirectUrl) => ({
        url: redirectUrl,
        dataLayerId: this.state.dataLayerId,
        dataTrackingId: 'shop-now',
        dataTrack: trackingLocation,
        dataTrackType: DATA_TRACK_TYPE_CTA,
      }),
    };

    const storeInfoTrack = {
      dataLayerId: this.state.dataLayerId,
      url: resolvedSecondaryUrl,
      dataTrack: trackingLocation,
      dataTrackingId: 'store-info',
      dataTrackType: DATA_TRACK_TYPE_INTERNAL,
    };

    const DesktopBody = () => {
      return (
        <AdDiv
          data-track-role={DATA_TRACK_ROLE_CONTAINER}
          data-layer-source-id={this.state.dataLayerId}
        >
          <AdColOne>
            <AdBodyDiv>
              <AdTagSpan id={`${shopSlug}_ad_tag`}>{name}</AdTagSpan>
              <AdShopContainerDiv>
                <AdShopImgContainerDiv>
                  <AdShopImg src={shopImgUrl} alt={mediumImageAltText} />
                </AdShopImgContainerDiv>
                {multiplier ? (
                  <PromoBadge
                    extraMilesText={shop.config.extraMilesText}
                    pluralFlatRateMilesText={pluralFlatRateMilesText}
                  />
                ) : null}
              </AdShopContainerDiv>
              <AdHeaderContainer>
              {headerText ? (
                <div>
                  <HeaderTextH2 aria-hidden="true">{headerText}</HeaderTextH2>
                  <ScreenReaderOnlyH1>
                    {screenReaderTextReplace(headerText, locale)}
                  </ScreenReaderOnlyH1>
                </div>
              ) : (
                <MilesOffer
                  className="miles-offer-extra-padding ad-large"
                  singularMilesText={singularMilesText}
                  pluralMilesText={pluralMilesText}
                  baseMiles={baseMiles}
                  multiplier={multiplier}
                  siteWideMultiplier={siteWideMultiplier}
                  singularFlatRateMilesText={singularFlatRateMilesText}
                  pluralFlatRateMilesText={pluralFlatRateMilesText}
                  milesBadgeText={milesBadgeText}
                  amountPerBaseMiles={amountPerBaseMiles}
                />
              )}
              </AdHeaderContainer>
              <SubHeaderTextDiv>{subHeaderText}</SubHeaderTextDiv>
              {(multiplier || siteWideMultiplier || expirationDate) && (
                <ExpiryTextDiv>
                  <DateDisplay
                    preText={expiryText}
                    dates={[
                      multiplier && multiplier.endDate,
                      siteWideMultiplier && siteWideMultiplier.endDate,
                      expirationDate,
                    ]}
                    locale={locale}
                  />
                </ExpiryTextDiv>
              )}
              {exclusionsApply && (
                <ExclusionsTextDiv>{exclusionsApplyText}</ExclusionsTextDiv>
              )}
              <ButtonRowDiv>
                <PinterestAnalyticsTracker
                  leadName={primaryButtonText}
                  shopSlug={shopSlug}
                >
                  <ShopNowButton
                    variant='secondary'
                    {...shopNowButtonProps}
                  />
                </PinterestAnalyticsTracker>
                <PinterestAnalyticsTracker
                  leadName={secondaryButtonText}
                  shopSlug={shopSlug}
                >
                  <Button
                    variant="tertiary"
                    size="medium"
                    buttonText={secondaryButtonText}
                    renderRightIcon={({ className }) => <ArrowRight className={className} />}
                    accessibleLabel={resolveAccessibleLabel(
                      secondaryButtonDescriptiveText,
                      shop,
                      linkTextPrepositions,
                      'for',
                      secondaryButtonText
                    )}
                    {...storeInfoTrack}
                  />
                </PinterestAnalyticsTracker>
              </ButtonRowDiv>
            </AdBodyDiv>
          </AdColOne>
          <AdImgContainerDiv>
            <AdImg src={adImgUrl} alt="" />
          </AdImgContainerDiv>
        </AdDiv>
      );
    };

    const MobileBody = () => {
      return (
        <AdMobileDiv
          data-track-role={DATA_TRACK_ROLE_CONTAINER}
          data-layer-source-id={this.state.dataLayerId}
        >
          <AdImgMobileDiv src={adImgUrl} alt="" />
          <AdBodyMobileDiv>
            <AdShopContainerDiv isMobile>
              <AdShopImgMobileContainerDiv>
                <AdShopImg src={shopImgUrl} alt={mediumImageAltText} />
              </AdShopImgMobileContainerDiv>
              {multiplier ? (
                <PromoBadge
                  extraMilesText={shop.config.extraMilesText}
                  pluralFlatRateMilesText={pluralFlatRateMilesText}
                />
              ) : null}
            </AdShopContainerDiv>
            <AdHeaderContainer>
            {headerText ? (
              <div>
                <HeaderTextMobileH2
                  aria-hidden="true"
                  len={headerText && headerText.length}
                >
                  {headerText}
                </HeaderTextMobileH2>
                <ScreenReaderOnlyH1>
                  {screenReaderTextReplace(headerText, locale, false)}
                </ScreenReaderOnlyH1>
              </div>
            ) : (
              <MilesOffer
                className="miles-offer-extra-padding ad-large-mobile"
                singularMilesText={singularMilesText}
                pluralMilesText={pluralMilesText}
                baseMiles={baseMiles}
                multiplier={multiplier}
                siteWideMultiplier={siteWideMultiplier}
                singularFlatRateMilesText={singularFlatRateMilesText}
                pluralFlatRateMilesText={pluralFlatRateMilesText}
                milesBadgeText={milesBadgeText}
                amountPerBaseMiles={amountPerBaseMiles}
              />
            )}
            </AdHeaderContainer>
            <SubHeaderTextMobileDiv>{subHeaderText}</SubHeaderTextMobileDiv>
            {(multiplier || siteWideMultiplier || expirationDate) && (
              <ExpiryTextDiv>
                <DateDisplay
                  preText={expiryText}
                  dates={[
                    multiplier && multiplier.endDate,
                    siteWideMultiplier && siteWideMultiplier.endDate,
                    expirationDate,
                  ]}
                  locale={locale}
                  className="medium"
                />
              </ExpiryTextDiv>
            )}
            {exclusionsApply && (
              <ExclusionsTextDiv>{exclusionsApplyText}</ExclusionsTextDiv>
            )}
            <ButtonColumnDiv isMobile>
              <PinterestAnalyticsTracker
                leadName={primaryButtonText}
                shopSlug={shopSlug}
              >
                <ShopNowButton
                  variant="primary"
                  fullWidth
                  {...shopNowButtonProps}
                />
              </PinterestAnalyticsTracker>
              <PinterestAnalyticsTracker
                leadName={secondaryButtonText}
                shopSlug={shopSlug}
              >
                <Button
                  variant="secondary"
                  size="medium"
                  theme="light"
                  fullWidth
                  buttonText={secondaryButtonText}
                  dataLayerId={this.state.dataLayerId}
                  accessibleLabel={resolveAccessibleLabel(
                    secondaryButtonDescriptiveText,
                    shop,
                    linkTextPrepositions,
                    'for',
                    secondaryButtonText
                  )}
                  {...storeInfoTrack}
                />
              </PinterestAnalyticsTracker>
            </ButtonColumnDiv>
          </AdBodyMobileDiv>
        </AdMobileDiv>
      );
    };

    const renderComp = (
      <MediaQueryHelper
        desktopChildren={DesktopBody()}
        mobileChildren={MobileBody()}
      />
    );
    return renderComp;
  }
}

export default AdLarge;
